import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../utils/apiClient';
import InfoModal from '../components/common/InfoModal';
import {
    Calculator,
    Loader2,
    AlertTriangle,
    CheckCircle2,
    Clock,
    Calendar,
    Play,
    RotateCcw,
    RefreshCw,
    Activity,
    History,
    Sparkles,
    XCircle,
    Timer,
    Lock
} from 'lucide-react';

const INTERVAL_PRESETS = [
    { label: '1 Saat', value: 60 },
    { label: '2 Saat', value: 120 },
    { label: '4 Saat', value: 240 },
    { label: '6 Saat', value: 360 },
    { label: '12 Saat', value: 720 },
    { label: '24 Saat', value: 1440 }
];

const LOOKBACK_PRESETS = [
    { label: '3 Gün', value: 3 },
    { label: '1 Hafta (7 Gün)', value: 7 },
    { label: '15 Gün', value: 15 },
    { label: '1 Ay (30 Gün)', value: 30 },
    { label: 'Maksimum (40 Gün)', value: 40 }
];

export default function PayrollCalcSettingsPage() {
    const { permissions } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [triggering, setTriggering] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);

    const [settings, setSettings] = useState({
        isEnabled: false,
        intervalMinutes: 60,
        lookbackDays: 7
    });

    const [statusData, setStatusData] = useState(null);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const canManageSettings = permissions.includes('settings:manage');

    const fetchStatus = useCallback(async () => {
        try {
            const st = await apiClient('PayrollSettings/status');
            setStatusData(st);
        } catch (err) {
            console.warn('Puantaj motor durumu alınamadı:', err);
        }
    }, []);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [data, st] = await Promise.all([
                apiClient('PayrollSettings/settings'),
                apiClient('PayrollSettings/status').catch(() => null)
            ]);
            setSettings({
                isEnabled: Boolean(data.isEnabled),
                intervalMinutes: data.intervalMinutes ?? 60,
                lookbackDays: data.lookbackDays ?? 7
            });
            if (st) setStatusData(st);
        } catch (err) {
            setError(err.payload?.message || err.message || 'Otomatik hesaplama ayarları alınamadı.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (canManageSettings) {
            fetchAll();
        } else {
            setLoading(false);
        }
    }, [canManageSettings, fetchAll]);

    // Çalışan bir iş varsa 2.5 saniyede bir, yoksa 15 saniyede bir canlı durumu güncelle
    useEffect(() => {
        if (!canManageSettings) return;
        const intervalMs = statusData?.isRunning ? 2500 : 15000;
        const timer = setInterval(() => {
            fetchStatus();
        }, intervalMs);
        return () => clearInterval(timer);
    }, [canManageSettings, statusData?.isRunning, fetchStatus]);

    const handleSave = async (e) => {
        e.preventDefault();
        const intervalNum = Number(settings.intervalMinutes);
        const lookbackNum = Number(settings.lookbackDays);

        if (!Number.isFinite(intervalNum) || intervalNum < 60) {
            setError('Hesaplama aralığı en az 60 dakika (1 saat) olmalıdır.');
            return;
        }
        if (!Number.isFinite(lookbackNum) || lookbackNum < 1 || lookbackNum > 40) {
            setError('Geriye dönük tarama gün sayısı 1 ile 40 arasında olmalıdır.');
            return;
        }
        if (lookbackNum >= 15 && intervalNum < 360) {
            setError('15 gün ve üzeri geriye dönük taramalarda sistem performansı için hesaplama aralığı en az 360 dakika (6 saat) olmalıdır.');
            return;
        }

        setSaving(true);
        setError('');
        setMessage('');

        try {
            const res = await apiClient('PayrollSettings/settings', {
                method: 'POST',
                body: {
                    isEnabled: Boolean(settings.isEnabled),
                    intervalMinutes: intervalNum,
                    lookbackDays: lookbackNum
                }
            });
            setSettings({
                isEnabled: Boolean(res.isEnabled),
                intervalMinutes: res.intervalMinutes,
                lookbackDays: res.lookbackDays
            });
            setMessage('Otomatik puantaj hesaplama ayarları başarıyla kaydedildi ve zamanlayıcı senkronize edildi.');
            fetchStatus();
        } catch (err) {
            setError(err.payload?.message || err.message || 'Ayarlar güncellenemedi.');
        } finally {
            setSaving(false);
        }
    };

    const handleTriggerNow = async () => {
        setTriggering(true);
        setError('');
        setMessage('');
        try {
            const res = await apiClient('PayrollSettings/trigger-now', { method: 'POST' });
            setMessage(res?.message || 'Puantaj hesaplama işlemi arka planda başlatıldı.');
            await fetchStatus();
        } catch (err) {
            setError(err.payload?.message || err.message || 'Hesaplama başlatılamadı.');
        } finally {
            setTriggering(false);
        }
    };

    const handleResetStuckJobs = async () => {
        setIsResetModalOpen(false);
        setResetting(true);
        setError('');
        setMessage('');
        try {
            const res = await apiClient('PayrollSettings/reset-stuck-jobs', { method: 'POST' });
            setMessage(res?.message || 'Askıda kalan işlemler sıfırlandı.');
            await fetchStatus();
        } catch (err) {
            setError(err.payload?.message || err.message || 'Askıdaki işlemler sıfırlanamadı.');
        } finally {
            setResetting(false);
        }
    };

    const formatDuration = (sec) => {
        if (sec === null || sec === undefined) return '-';
        if (sec < 60) return `${sec} sn`;
        const mins = Math.floor(sec / 60);
        const rem = sec % 60;
        return rem > 0 ? `${mins} dk ${rem} sn` : `${mins} dk`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
        );
    }

    if (!canManageSettings) {
        return (
            <div className="p-8">
                <div className="bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-400 p-6 rounded-2xl border border-rose-200 dark:border-rose-800/50 flex items-center gap-3 max-w-2xl mx-auto">
                    <AlertTriangle className="w-6 h-6 shrink-0" />
                    <p className="font-medium text-sm">Bu sayfayı görüntüleme ve düzenleme yetkiniz bulunmamaktadır.</p>
                </div>
            </div>
        );
    }

    const isRunning = Boolean(statusData?.isRunning);
    const progressPercent = Math.max(0, Math.min(100, Number(statusData?.progressPercent) || 0));
    const recentRuns = Array.isArray(statusData?.recentRuns) ? statusData.recentRuns : [];
    const isHeavyLookback = Number(settings.lookbackDays) >= 15;
    const minAllowedInterval = isHeavyLookback ? 360 : 60;

    const handleSelectLookbackDays = (daysVal) => {
        setSettings(prev => {
            const parsedDays = daysVal === '' ? '' : parseInt(daysVal, 10);
            const nextInterval = (Number(parsedDays) >= 15 && Number(prev.intervalMinutes) < 360)
                ? 360
                : prev.intervalMinutes;
            return {
                ...prev,
                lookbackDays: parsedDays,
                intervalMinutes: nextInterval
            };
        });
    };

    return (
        <div className="space-y-5">
            {/* Üst Başlık ve Hızlı Aksiyonlar */}
            <div className="bg-white dark:bg-[#18191c] p-5 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-start gap-3.5">
                        <div className="w-11 h-11 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200/70 dark:border-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                            <Calculator className="w-6 h-6" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2.5 flex-wrap">
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                    Otomatik Puantaj Hesaplama Ayarları
                                </h2>
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                                    isRunning
                                        ? 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-500/30 animate-pulse'
                                        : settings.isEnabled
                                            ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30'
                                            : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700'
                                }`}>
                                    {isRunning ? (
                                        <>
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                            Hesaplama Çalışıyor (%{progressPercent})
                                        </>
                                    ) : settings.isEnabled ? (
                                        <>
                                            <CheckCircle2 className="w-3.5 h-3.5" />
                                            Otomatik Motor Aktif
                                        </>
                                    ) : (
                                        <>
                                            <XCircle className="w-3.5 h-3.5" />
                                            Otomatik Motor Pasif
                                        </>
                                    )}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Personel geçiş kayıtlarının, vardiyaların ve izinlerin arka planda periyodik olarak taranıp puantaj tablolarının güncel tutulmasını yönetin.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={fetchStatus}
                            className="p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#111214] text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            title="Durumu Yenile"
                        >
                            <RefreshCw className="w-4 h-4" />
                        </button>

                        {isRunning && (
                            <button
                                type="button"
                                onClick={() => setIsResetModalOpen(true)}
                                disabled={resetting}
                                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-500/20 border border-amber-200 dark:border-amber-500/30 transition-colors disabled:opacity-50"
                            >
                                <RotateCcw className="w-3.5 h-3.5" />
                                {resetting ? 'Sıfırlanıyor...' : 'Askıdaki İşlemi Sıfırla'}
                            </button>
                        )}

                        <button
                            type="button"
                            onClick={handleTriggerNow}
                            disabled={triggering || isRunning}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {triggering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                            Şimdi Hesaplamayı Başlat (Son {settings.lookbackDays || 7} Gün)
                        </button>
                    </div>
                </div>

                {/* Canlı İlerleme Çubuğu (Eğer şu an çalışıyorsa) */}
                {isRunning && (
                    <div className="mt-5 p-4 rounded-xl bg-indigo-50/70 dark:bg-indigo-950/25 border border-indigo-200 dark:border-indigo-800/50 space-y-2.5">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                            <div className="flex items-center gap-2 font-semibold text-indigo-900 dark:text-indigo-200">
                                <Loader2 className="w-4 h-4 animate-spin text-indigo-600 dark:text-indigo-400" />
                                <span>
                                    Arka plan puantaj hesaplaması devam ediyor...
                                    {statusData?.totalEmployees > 0 && (
                                        <span className="ml-1.5 font-mono">
                                            ({statusData.processedEmployees} / {statusData.totalEmployees} personel)
                                        </span>
                                    )}
                                </span>
                            </div>
                            <div className="flex items-center gap-3 text-indigo-700 dark:text-indigo-300 font-semibold">
                                {statusData?.etaSeconds > 0 && (
                                    <span>Tahmini Kalan: ~{formatDuration(statusData.etaSeconds)}</span>
                                )}
                                <span className="text-sm font-bold">%{progressPercent}</span>
                            </div>
                        </div>
                        <div className="w-full h-2.5 bg-indigo-200/70 dark:bg-indigo-950 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-indigo-500 to-emerald-500 rounded-full transition-all duration-500"
                                style={{ width: `${Math.max(4, progressPercent)}%` }}
                            />
                        </div>
                    </div>
                )}

                {/* Canlı Durum KPI Kartları */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 mt-5">
                    <div className="p-3.5 rounded-xl bg-gray-50/80 dark:bg-[#111214] border border-gray-200/80 dark:border-gray-800">
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                            <span>Son Tamamlanma</span>
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        </div>
                        <p className="text-base font-bold text-gray-900 dark:text-white mt-1.5">
                            {statusData?.lastCompletedAtStr || '-'}
                        </p>
                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                            Son Durum: {statusData?.lastStatus === 'Completed' ? 'Başarılı' : statusData?.lastStatus === 'Failed' ? 'Hatalı' : statusData?.lastStatus === 'Running' ? 'Çalışıyor' : 'Henüz Çalışmadı'}
                        </span>
                    </div>

                    <div className="p-3.5 rounded-xl bg-gray-50/80 dark:bg-[#111214] border border-gray-200/80 dark:border-gray-800">
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                            <span>Sonraki Planlanan Çalışma</span>
                            <Timer className="w-4 h-4 text-indigo-500" />
                        </div>
                        <p className="text-base font-bold text-gray-900 dark:text-white mt-1.5">
                            {settings.isEnabled ? (statusData?.nextPlannedAtStr || 'İlk döngüde çalışacak') : 'Otomatik Motor Kapalı'}
                        </p>
                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                            Periyot: Her {settings.intervalMinutes || 60} dakikada bir
                        </span>
                    </div>

                    <div className="p-3.5 rounded-xl bg-gray-50/80 dark:bg-[#111214] border border-gray-200/80 dark:border-gray-800">
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                            <span>Geriye Dönük Tarama</span>
                            <Calendar className="w-4 h-4 text-amber-500" />
                        </div>
                        <p className="text-base font-bold text-gray-900 dark:text-white mt-1.5">
                            Son {settings.lookbackDays || 7} Gün
                        </p>
                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                            Hafta tatili için pazartesiye hizalanır
                        </span>
                    </div>

                    <div className="p-3.5 rounded-xl bg-gray-50/80 dark:bg-[#111214] border border-gray-200/80 dark:border-gray-800">
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                            <span>Son Çalışmada İşlenen</span>
                            <Activity className="w-4 h-4 text-emerald-500" />
                        </div>
                        <p className="text-base font-bold text-gray-900 dark:text-white mt-1.5">
                            {statusData?.totalEmployees > 0
                                ? `${statusData.processedEmployees} / ${statusData.totalEmployees} Personel`
                                : '-'}
                        </p>
                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                            Başlangıç: {statusData?.lastRunStartedAtStr || '-'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Bildirimler */}
            {error && (
                <div className="p-4 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/50 rounded-2xl flex items-center justify-between gap-3 text-rose-700 dark:text-rose-300 text-xs font-medium">
                    <div className="flex items-center gap-2.5">
                        <AlertTriangle className="w-4 h-4 shrink-0" />
                        <span>{error}</span>
                    </div>
                    <button type="button" onClick={() => setError('')} className="font-bold">✕</button>
                </div>
            )}
            {message && (
                <div className="p-4 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/50 rounded-2xl flex items-center justify-between gap-3 text-emerald-700 dark:text-emerald-300 text-xs font-medium">
                    <div className="flex items-center gap-2.5">
                        <CheckCircle2 className="w-4 h-4 shrink-0" />
                        <span>{message}</span>
                    </div>
                    <button type="button" onClick={() => setMessage('')} className="font-bold">✕</button>
                </div>
            )}

            {/* Ayarlar Formu */}
            <form onSubmit={handleSave} className="bg-white dark:bg-[#18191c] p-5 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 space-y-6">
                {/* Otomatik Hesaplama Aktif/Pasif Kartı */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 bg-gray-50/80 dark:bg-[#111214] rounded-xl border border-gray-200/80 dark:border-gray-800">
                    <div>
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white">
                            Periyodik Otomatik Puantaj Hesaplama Motoru
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-2xl leading-relaxed">
                            Aktif edildiğinde Hangfire orkestratörü belirlediğiniz dakika aralığında tüm aktif personellerin puantajını geriye dönük tarayarak otomatik günceller.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 gap-1.5 bg-gray-200/80 dark:bg-[#18191c] p-1 rounded-xl border border-gray-200 dark:border-gray-800 w-full md:w-64 shrink-0">
                        <button
                            type="button"
                            onClick={() => setSettings(prev => ({ ...prev, isEnabled: false }))}
                            disabled={saving}
                            className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                !settings.isEnabled
                                    ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm border border-gray-200/80 dark:border-gray-700'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                            }`}
                        >
                            Kapalı (Pasif)
                        </button>
                        <button
                            type="button"
                            onClick={() => setSettings(prev => ({ ...prev, isEnabled: true }))}
                            disabled={saving}
                            className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                settings.isEnabled
                                    ? 'bg-emerald-600 text-white shadow-sm'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                            }`}
                        >
                            Açık (Aktif)
                        </button>
                    </div>
                </div>

                {/* Dakika Aralığı ve Geriye Dönük Gün Ayarları */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {/* Hesaplama Aralığı */}
                    <div className="p-4 bg-gray-50/80 dark:bg-[#111214] rounded-xl border border-gray-200/80 dark:border-gray-800 space-y-3">
                        <label className="flex items-center justify-between text-xs font-bold text-gray-800 dark:text-gray-100">
                            <span className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-emerald-500" />
                                Hesaplama Aralığı (Dakika)
                            </span>
                            <span className={`text-[11px] font-semibold ${isHeavyLookback ? 'text-amber-600 dark:text-amber-400' : 'font-normal text-gray-500'}`}>
                                Min: {minAllowedInterval} dk ({isHeavyLookback ? '6 Saat - 15+ Gün Kuralı' : '1 Saat'})
                            </span>
                        </label>

                        <input
                            type="number"
                            min={minAllowedInterval}
                            max="10080"
                            className="w-full p-2.5 bg-white dark:bg-[#18191c] border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none text-sm font-semibold text-gray-900 dark:text-white"
                            value={settings.intervalMinutes}
                            onChange={(e) => {
                                const val = e.target.value;
                                setSettings(prev => ({
                                    ...prev,
                                    intervalMinutes: val === '' ? '' : parseInt(val, 10)
                                }));
                            }}
                            disabled={saving}
                            required
                        />

                        <div className="flex flex-wrap items-center gap-1.5 pt-1">
                            <span className="text-[11px] text-gray-400 mr-1 flex items-center gap-1">
                                <Sparkles className="w-3 h-3" /> Hızlı Seçim:
                            </span>
                            {INTERVAL_PRESETS.map(p => {
                                const isRestricted = isHeavyLookback && p.value < 360;
                                const isSelected = Number(settings.intervalMinutes) === p.value;
                                return (
                                    <button
                                        key={p.value}
                                        type="button"
                                        disabled={isRestricted}
                                        title={isRestricted ? '15 gün ve üzeri taramalarda en az 6 saat (360 dk) seçilebilir.' : `${p.label} (${p.value} dk)`}
                                        onClick={() => !isRestricted && setSettings(prev => ({ ...prev, intervalMinutes: p.value }))}
                                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold border transition-colors ${
                                            isSelected
                                                ? 'bg-emerald-600 border-emerald-600 text-white'
                                                : isRestricted
                                                    ? 'bg-gray-100 dark:bg-gray-900 border-gray-200 dark:border-gray-800 text-gray-400 dark:text-gray-600 opacity-60 cursor-not-allowed line-through'
                                                    : 'bg-white dark:bg-[#18191c] border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-emerald-400'
                                        }`}
                                    >
                                        {isRestricted && <Lock className="w-2.5 h-2.5" />}
                                        {p.label}
                                    </button>
                                );
                            })}
                        </div>

                        {isHeavyLookback ? (
                            <p className="text-[11px] text-amber-600 dark:text-amber-400 font-medium leading-relaxed">
                                Not: 15, 30 ve 40 günlük geniş kapsamlı taramalarda sistem kaynaklarını korumak için yalnızca <strong>6 saat (360 dk) ve üzeri</strong> aralıklar seçilebilir.
                            </p>
                        ) : (
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
                                İki otomatik puantaj hesaplaması arasında beklenecek süreyi belirler. Sunucu performansı için minimum 60 dakika (1 saat) zorunludur.
                            </p>
                        )}
                    </div>

                    {/* Geriye Dönük Gün Sayısı */}
                    <div className="p-4 bg-gray-50/80 dark:bg-[#111214] rounded-xl border border-gray-200/80 dark:border-gray-800 space-y-3">
                        <label className="flex items-center justify-between text-xs font-bold text-gray-800 dark:text-gray-100">
                            <span className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-indigo-500" />
                                Geriye Dönük Tarama Gün Sayısı
                            </span>
                            <span className="text-[11px] font-normal text-gray-500">1 - 40 Gün</span>
                        </label>

                        <input
                            type="number"
                            min="1"
                            max="40"
                            className="w-full p-2.5 bg-white dark:bg-[#18191c] border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-semibold text-gray-900 dark:text-white"
                            value={settings.lookbackDays}
                            onChange={(e) => handleSelectLookbackDays(e.target.value)}
                            disabled={saving}
                            required
                        />

                        <div className="flex flex-wrap items-center gap-1.5 pt-1">
                            <span className="text-[11px] text-gray-400 mr-1 flex items-center gap-1">
                                <Sparkles className="w-3 h-3" /> Hızlı Seçim:
                            </span>
                            {LOOKBACK_PRESETS.map(p => (
                                <button
                                    key={p.value}
                                    type="button"
                                    onClick={() => handleSelectLookbackDays(p.value)}
                                    className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold border transition-colors ${
                                        Number(settings.lookbackDays) === p.value
                                            ? 'bg-indigo-600 border-indigo-600 text-white'
                                            : 'bg-white dark:bg-[#18191c] border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-indigo-400'
                                    }`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>

                        <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
                            Geçmişe dönük girilen izinlerin veya gecikmeli gelen cihaz loglarının puantaja yansıması için bugün dahil kaç gün geriye taranacağını belirler. (15+ gün seçiminde periyot en az 6 saat olur).
                        </p>
                    </div>
                </div>

                {/* Kaydet Butonu */}
                <div className="flex items-center justify-end pt-2 border-t border-gray-100 dark:border-gray-800">
                    <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold rounded-xl shadow-sm transition-all"
                    >
                        {saving ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Kaydediliyor...
                            </>
                        ) : (
                            <>
                                <CheckCircle2 className="w-4 h-4" />
                                Ayarları Kaydet ve Uygula
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Son 5 Hesaplama Geçmişi Tablosu */}
            <div className="bg-white dark:bg-[#18191c] p-5 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2.5">
                        <History className="w-5 h-5 text-indigo-500" />
                        <div>
                            <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                                Son Puantaj Hesaplama Çalışmaları (Son 5 Kayıt)
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Otomatik zamanlayıcı veya manuel tetikleme ile çalışan son puantaj hesaplama işlemleri.
                            </p>
                        </div>
                    </div>
                </div>

                {recentRuns.length === 0 ? (
                    <div className="p-6 text-center text-xs text-gray-500 dark:text-gray-400 bg-gray-50/70 dark:bg-[#111214] rounded-xl border border-gray-200/70 dark:border-gray-800">
                        Henüz kaydedilmiş bir puantaj hesaplama çalışması bulunmuyor.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 uppercase text-[11px]">
                                    <th className="py-2.5 px-3">Başlangıç (TR)</th>
                                    <th className="py-2.5 px-3">Bitiş (TR)</th>
                                    <th className="py-2.5 px-3">Süre</th>
                                    <th className="py-2.5 px-3">Tetikleyici</th>
                                    <th className="py-2.5 px-3">Tarih Aralığı</th>
                                    <th className="py-2.5 px-3">Personel</th>
                                    <th className="py-2.5 px-3">Durum</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                {recentRuns.map(run => (
                                    <tr key={run.id} className="hover:bg-gray-50/70 dark:hover:bg-[#111214]">
                                        <td className="py-2.5 px-3 font-mono text-gray-800 dark:text-gray-200 whitespace-nowrap">
                                            {run.startedAtStr}
                                        </td>
                                        <td className="py-2.5 px-3 font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                            {run.completedAtStr}
                                        </td>
                                        <td className="py-2.5 px-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                            {formatDuration(run.durationSeconds)}
                                        </td>
                                        <td className="py-2.5 px-3 whitespace-nowrap">
                                            <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold ${
                                                run.triggerType === 'Scheduled'
                                                    ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                                                    : 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300'
                                            }`}>
                                                {run.triggerType === 'Scheduled' ? 'Otomatik Zamanlayıcı' : 'Manuel Tetikleme'}
                                            </span>
                                        </td>
                                        <td className="py-2.5 px-3 font-mono text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                            {run.targetRangeStr}
                                        </td>
                                        <td className="py-2.5 px-3 font-semibold text-gray-800 dark:text-gray-200 whitespace-nowrap">
                                            {run.totalEmployees > 0 ? `${run.processedEmployees} / ${run.totalEmployees}` : '-'}
                                        </td>
                                        <td className="py-2.5 px-3">
                                            <div className="flex flex-col gap-1">
                                                {run.status === 'Completed' && (
                                                    <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-semibold">
                                                        <CheckCircle2 className="w-3.5 h-3.5" /> Tamamlandı
                                                    </span>
                                                )}
                                                {run.status === 'Running' && (
                                                    <span className="inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400 font-semibold">
                                                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Çalışıyor (%{run.progressPercent})
                                                    </span>
                                                )}
                                                {run.status === 'Failed' && (
                                                    <span className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-400 font-semibold" title={run.errorMessage}>
                                                        <XCircle className="w-3.5 h-3.5" /> Hatalı
                                                    </span>
                                                )}
                                                {run.errorMessage && (
                                                    <span className="text-[11px] text-rose-500 dark:text-rose-400 max-w-xs truncate" title={run.errorMessage}>
                                                        {run.errorMessage}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Askıdaki İşlemi Sıfırlama Onay Modalı */}
            <InfoModal
                isOpen={isResetModalOpen}
                onClose={() => setIsResetModalOpen(false)}
                title="Askıda Kalan Puantaj İşlemini Sıfırla"
                message="Şu anda 'Çalışıyor' (Running) durumunda görünen puantaj hesaplama işlemini iptal edip sıfırlamak istediğinize emin misiniz? Bu işlem yalnızca gerçekten takılı kalmış işler için kullanılmalıdır."
                type="confirm"
                onConfirm={handleResetStuckJobs}
                confirmText="Evet, İşlemi Sıfırla"
                cancelText="Vazgeç"
            />
        </div>
    );
}
